// src/types/opportunity.ts
//
// Shared types for the Opportunity Engine (Phase 1). Mirrors the row
// shape in `docs/design/sql_schema_opportunity.md`. UI + client +
// (in-browser) calc helpers all consume these.

/**
 * Free-form product type tag. The picker reads Ops Deal Types
 * (`ops_deal_product_types.type_key`). The closed enum that previously
 * lived here (`red_iron|shed|steel|other`) was retired on 2026-06-16
 * when the DB CHECK constraint dropped — see
 * `docs/sql/opp_product_type_freeform_v1.sql`.
 *
 * The well-known sentinel `OPPORTUNITY_PRODUCT_TYPE_OTHER` ("other") is
 * appended to the dropdown so a rep can capture an opportunity for a
 * product line the org hasn't configured yet. Server-side validation
 * accepts any non-empty tag up to 64 chars.
 */
export type OpportunityProductType = string;

export const OPPORTUNITY_PRODUCT_TYPE_OTHER = "other";
export const OPPORTUNITY_PRODUCT_TYPE_OTHER_LABEL = "Other";
export const OPPORTUNITY_PRODUCT_TYPE_MAX_LEN = 64;

export const OPPORTUNITY_STATUSES = [
  "new",
  "quoting",
  "revision_requested",
  "pending_signature",
  "signed",
  "ordered",
  "in_process",
  "complete",
  "lost",
] as const;
export type OpportunityStatus = (typeof OPPORTUNITY_STATUSES)[number];

export const OPPORTUNITY_STATUS_LABELS: Record<OpportunityStatus, string> = {
  new: "New",
  quoting: "Quoting",
  revision_requested: "Revision requested",
  pending_signature: "Pending signature",
  signed: "Signed",
  ordered: "Ordered",
  in_process: "In process",
  complete: "Complete",
  lost: "Lost",
};

/** An ad-hoc line item on a pricing snapshot. */
export type OpportunityAdditionalItem = {
  label: string;
  amount: number;
  taxable?: boolean;
  note?: string;
};

/** Rep-entered subtotal adjustment (slice 7c — sales_adjustments jsonb). */
export type OpportunitySalesAdjustment = {
  description: string;
  amount_mode: OpportunityAmountMode;
  value: number;
};

/**
 * Which pricing format a snapshot was authored under. Drives both the
 * UI (`OpportunityPricingFormRedIron` vs `…FormGeneric`) and the PDF
 * sections (cost-breakdown + payment-schedule branch on this key).
 * Added 2026-06-16 by `docs/sql/opp_generic_format_v1.sql`.
 */
export type OpportunityFormatKey = "red_iron" | "generic";

/** Persist-style flag for the tax + deposit `$ vs %` toggles. */
export type OpportunityAmountMode = "amount" | "percent";

/** Raw inputs the salesperson types into the pricing form. */
export type OpportunityPricingInputs = {
  format_key: OpportunityFormatKey;
  // ---- Red-Iron line items (zero on generic-format snapshots) ----
  building_materials_cost: number;
  erection_cost: number;
  concrete_cost: number;
  engineering_cost: number;
  permit_assistance_cost: number;
  additional_items: OpportunityAdditionalItem[];
  // ---- Percentages used by both formats ----
  commission_pct: number;
  margin_pct: number;
  deposit_pct: number;
  // ---- Generic format ----
  /** Single subtotal figure for generic-format snapshots (zero on red_iron). */
  subtotal_input: number;
  /** Whether `tax_value` is a dollar amount or a percentage of the subtotal. */
  tax_mode: OpportunityAmountMode;
  /** Raw input from the rep (dollars or percentage depending on `tax_mode`). */
  tax_value: number;
  /** Whether the deposit is a dollar amount or a percentage of total sale price. */
  deposit_mode: OpportunityAmountMode;
  /** Raw input from the rep when `deposit_mode === "amount"`; ignored otherwise. */
  deposit_amount_input: number;
  /** Rep-entered subtotal adjustments (7c). */
  sales_adjustments: OpportunitySalesAdjustment[];
  /** Required when deposit is entered as a manual $ amount (deviation from % rule). */
  deposit_deviation_note: string | null;
  /** Rep-edited amounts for manager calc lines (`line id` → dollars or %). */
  format_calc_values: Record<string, number>;
  /**
   * Per-revision override of a manager calc line's amount mode
   * (`line id` → "fixed" | "percent"). Only deviating lines are present;
   * absent lines use the manager-configured mode.
   */
  format_calc_modes: Record<string, "fixed" | "percent">;
  /** Required note per line when a percent line is forced to dollars. */
  format_calc_deviation_notes: Record<string, string>;
};

/** Derived totals computed from `OpportunityPricingInputs`. */
export type OpportunityPricingTotals = {
  total_cost: number;
  margin_amount: number;
  commission_amount: number;
  total_sale_price: number;
  deposit_amount: number;
  remaining_balance: number;
  /** Denormalized dollar value of the generic-format tax line; zero on red_iron. */
  tax_amount: number;
};

/** Full pricing-snapshot row (DB shape — `opp_pricing_snapshots`). */
export type OpportunityPricingSnapshot = OpportunityPricingInputs &
  OpportunityPricingTotals & {
    id: string;
    organization_id: string;
    opportunity_id: string;
    revision_number: number;
    created_by_user_id: string | null;
    created_at: string;
  };

/**
 * Loose accessor — older snapshot rows persisted before slice B
 * (opp_engine iter 2) lack the generic-format columns. The `??`
 * fallbacks here keep render code tolerant of mixed-vintage rows.
 */
export function snapshotFormatKey(
  snapshot: Pick<OpportunityPricingSnapshot, "format_key"> | null | undefined,
): OpportunityFormatKey {
  if (!snapshot) return "red_iron";
  return snapshot.format_key === "generic" ? "generic" : "red_iron";
}

/** Revision row (DB shape — `opp_revisions`). */
export type OpportunityRevision = {
  id: string;
  organization_id: string;
  opportunity_id: string;
  revision_number: number;
  pricing_snapshot_id: string;
  notes: string | null;
  created_by_user_id: string | null;
  created_at: string;
};

/** Opportunity row (DB shape — `opp_opportunities`). */
export type Opportunity = {
  id: string;
  organization_id: string;
  lead_id: string | null;
  location_id: string | null;
  assigned_user_id: string | null;
  product_type: OpportunityProductType;
  /** Instance fulfillment. Null/omit until Slice C SQL is applied or the field is set. */
  fulfillment?: "inventory" | "custom" | null;
  mfg_unit_id?: string | null;
  /** Present on get/update-fulfillment after joining the attached unit. */
  mfg_unit_serial?: string | null;
  status: OpportunityStatus;
  project_name: string | null;
  customer_name: string | null;
  customer_company: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  customer_address: string | null;
  use_rep_proposal_contact: boolean;
  active_revision_id: string | null;
  created_by_user_id: string | null;
  external_ghl_opportunity_id: string | null;
  external_ghl_contact_id: string | null;
  crm_pushed_at: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  scheduling_notes: string;
};

/** Document category / slot key (manager-configurable; stored on `opp_documents.category`). */
export type OpportunityDocumentCategory = string;

/** Legacy red-iron slot keys — use manager document slots for labels in UI. */
export const OPPORTUNITY_DOC_CATEGORIES = ["nucor", "erector", "financing", "customer"] as const;

export const OPPORTUNITY_DOC_CATEGORY_LABELS: Record<string, string> = {
  document: "Document",
  nucor: "Nucor",
  erector: "Erector",
  financing: "Financing",
  customer: "Customer",
};

/** Output type enum (DB CHECK on `opp_generated_outputs.output_type`). */
export const OPPORTUNITY_OUTPUT_TYPES = [
  "proposal_package",
  "contract_package",
  "cover_letter",
  "cost_breakdown",
  "payment_schedule",
  "intent_to_purchase",
] as const;
export type OpportunityOutputType = (typeof OPPORTUNITY_OUTPUT_TYPES)[number];

export const OPPORTUNITY_OUTPUT_TYPE_LABELS: Record<OpportunityOutputType, string> = {
  proposal_package: "Proposal package",
  contract_package: "Contract package",
  cover_letter: "Cover letter",
  cost_breakdown: "Cost breakdown",
  payment_schedule: "Payment schedule",
  intent_to_purchase: "Intent to purchase",
};
